import mapboxgl from 'mapbox-gl';
import turfCircle from '@turf/circle';

/**
 * Camada de compatibilidade `google.maps` → Mapbox GL.
 *
 * A tela do georreferenciamento (DefaulMap, MapaService, DrawToolBar, ShapesAttributesEditor…)
 * guarda e manipula as formas desenhadas como objetos nativos do Google (Circle, Polygon,
 * Rectangle) e fala com o mapa por `window.googleMap`. Para trocar só o mapa sem mexer no
 * resto, este módulo reproduz o subconjunto dessa API que o código usa, desenhando as formas
 * em camadas do Mapbox. Só é instalado quando `window.geoMapEngine === 'mapbox'`; o mapa
 * legado (Google) continua carregando a API original.
 */

const SHAPES_SOURCE = 'geo-shapes';
const SHAPES_FILL_LAYER = 'geo-shapes-fill';
const SHAPES_LINE_LAYER = 'geo-shapes-line';
const EARTH_RADIUS = 6371008.8;

const EMPTY_FC = { type: 'FeatureCollection', features: [] };

/* ============================== Geometria básica ============================== */

export class LatLng {
    constructor(lat, lng) {
        this._lat = Number(lat);
        this._lng = Number(lng);
    }

    lat() { return this._lat; }

    lng() { return this._lng; }

    toUrlValue(precision = 6) {
        return `${+this._lat.toFixed(precision)},${+this._lng.toFixed(precision)}`;
    }

    toJSON() { return { lat: this._lat, lng: this._lng }; }
}

export const toLatLng = (value) => {
    if (value instanceof LatLng) return value;
    if (value && typeof value.lat === 'function') return new LatLng(value.lat(), value.lng());
    return new LatLng(value.lat, value.lng);
};

export class LatLngBounds {
    constructor(sw, ne) {
        this._empty = true;
        if (sw) this.extend(sw);
        if (ne) this.extend(ne);
    }

    extend(point) {
        const { _lat: lat, _lng: lng } = toLatLng(point);

        if (this._empty) {
            this._s = lat; this._n = lat; this._w = lng; this._e = lng;
            this._empty = false;
        } else {
            this._s = Math.min(this._s, lat);
            this._n = Math.max(this._n, lat);
            this._w = Math.min(this._w, lng);
            this._e = Math.max(this._e, lng);
        }

        return this;
    }

    isEmpty() { return this._empty; }

    getNorthEast() { return new LatLng(this._n, this._e); }

    getSouthWest() { return new LatLng(this._s, this._w); }

    getCenter() { return new LatLng((this._n + this._s) / 2, (this._e + this._w) / 2); }
}

const haversineMeters = (a, b) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat() - a.lat());
    const dLng = toRad(b.lng() - a.lng());
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(a.lat())) * Math.cos(toRad(b.lat())) * Math.sin(dLng / 2) ** 2;

    return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(h));
};

// Ponto a `meters` metros de `center`, na direção leste.
const eastOf = (center, meters) => {
    const dLng = (meters / (EARTH_RADIUS * Math.cos((center.lat() * Math.PI) / 180))) * (180 / Math.PI);
    return new LatLng(center.lat(), center.lng() + dLng);
};

/* ================================== Eventos ================================== */

class Emitter {
    _on(name, fn) {
        this._listeners = this._listeners || {};
        (this._listeners[name] = this._listeners[name] || []).push(fn);

        return { remove: () => { this._listeners[name] = this._listeners[name].filter((f) => f !== fn); } };
    }

    _emit(name, ...args) {
        (this._listeners?.[name] || []).slice().forEach((fn) => fn(...args));
    }
}

const event = {
    addListener(target, name, fn) { return target._on(name, fn); },
    clearInstanceListeners(target) { target._listeners = {}; },
};

// Equivalente ao MVCArray do Google para o caminho de um polígono.
class MVCPath extends Emitter {
    constructor(points = []) {
        super();
        this._points = points.map(toLatLng);
    }

    getLength() { return this._points.length; }

    getAt(i) { return this._points[i]; }

    getArray() { return this._points.slice(); }

    forEach(fn) { this._points.forEach(fn); }

    setAt(i, point) {
        this._points[i] = toLatLng(point);
        this._emit('set_at', i);
    }

    insertAt(i, point) {
        this._points.splice(i, 0, toLatLng(point));
        this._emit('insert_at', i);
    }

    removeAt(i) {
        this._points.splice(i, 1);
        this._emit('remove_at', i);
    }
}

/* ================================== Formas =================================== */

let shapeUid = 0;

/**
 * Base das formas. Como no Google, as opções passadas ao construtor / `setOptions` viram
 * propriedades do próprio objeto (o app lê `shape.fillColor`, `shape.strokeColor` etc.).
 */
class Shape extends Emitter {
    constructor(options = {}) {
        super();
        this._uid = ++shapeUid;
        this._map = null;
        this._handles = [];
        const { map, ...rest } = options;
        Object.assign(this, rest);
        this._pendingMap = map || null;
    }

    getMap() { return this._map; }

    setMap(map) {
        if (this._map === map) return;

        if (this._map) {
            this._clearHandles();
            this._map._detach(this);
        }

        this._map = map || null;

        if (this._map) {
            this._everAttached = true;
            this._map._attach(this);
        }
    }

    setOptions(options = {}) {
        Object.assign(this, options);
        this._changed();
        this._renderHandles();
    }

    _changed() {
        if (this._map) this._map._refreshShapes();
    }

    _styleProps() {
        const fill = this.fillColor;
        const transparent = !fill || fill === 'transparent';

        return {
            uid: this._uid,
            fillColor: transparent ? '#000000' : fill,
            fillOpacity: transparent ? 0 : (this.fillOpacity ?? 0.5),
            strokeColor: this.strokeColor || '#000000',
            strokeWeight: this.strokeWeight ?? 3,
        };
    }

    _feature() {
        const ring = this._ring();
        if (!ring || ring.length < 3) return null;

        return {
            type: 'Feature',
            properties: this._styleProps(),
            geometry: { type: 'Polygon', coordinates: [ring] },
        };
    }

    _clearHandles() {
        this._handles.forEach((marker) => marker.remove());
        this._handles = [];
    }

    _makeHandle(lngLat, className, { onDrag, onDragEnd } = {}) {
        const element = document.createElement('div');
        element.className = className;

        const marker = new mapboxgl.Marker({ element, draggable: true })
            .setLngLat(lngLat)
            .addTo(this._map._mapbox);

        if (onDrag) marker.on('drag', () => onDrag(marker.getLngLat()));
        if (onDragEnd) marker.on('dragend', () => onDragEnd(marker.getLngLat()));
        this._handles.push(marker);

        return marker;
    }

    // Reposiciona as alças depois de um redesenho; recriadas quando a estrutura muda.
    _renderHandles() {
        this._clearHandles();
        if (this._map && this.editable) this._buildHandles();
    }

    _buildHandles() {}
}

export class Circle extends Shape {
    constructor(options = {}) {
        super(options);
        this.center = toLatLng(options.center || { lat: 0, lng: 0 });
        this.radius = Number(options.radius || 0);
        if (this._pendingMap) this.setMap(this._pendingMap);
    }

    getCenter() { return this.center; }

    getRadius() { return this.radius; }

    setCenter(center) {
        this.center = toLatLng(center);
        this._changed();
        this._emit('center_changed');
    }

    setRadius(radius) {
        const next = Number(radius);
        if (Number.isNaN(next) || next === this.radius) return;
        this.radius = next;
        this._changed();
        this._renderHandles();
        this._emit('radius_changed');
    }

    setOptions(options = {}) {
        const { center, radius, ...rest } = options;
        const previousRadius = this.radius;
        Object.assign(this, rest);
        if (center) this.center = toLatLng(center);
        if (radius !== undefined && !Number.isNaN(Number(radius))) this.radius = Number(radius);
        this._changed();
        this._renderHandles();
        if (this.radius !== previousRadius) this._emit('radius_changed');
    }

    getBounds() {
        const bounds = new LatLngBounds();
        const ring = this._ring() || [];
        ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }));

        return bounds;
    }

    _ring() {
        if (!(this.radius > 0)) return null;

        return turfCircle([this.center.lng(), this.center.lat()], this.radius, { steps: 72, units: 'meters' })
            .geometry.coordinates[0];
    }

    _buildHandles() {
        const center = this._makeHandle([this.center.lng(), this.center.lat()], 'geo-mb-handle', {
            onDrag: (ll) => { this.center = new LatLng(ll.lat, ll.lng); this._map._refreshShapes(false); this._moveRadiusHandle(); },
            onDragEnd: () => { this._changed(); this._renderHandles(); this._emit('center_changed'); },
        });

        const edge = eastOf(this.center, this.radius);
        this._radiusHandle = this._makeHandle([edge.lng(), edge.lat()], 'geo-mb-handle', {
            onDrag: (ll) => {
                this.radius = Math.max(1, haversineMeters(this.center, new LatLng(ll.lat, ll.lng)));
                this._map._refreshShapes(false);
            },
            onDragEnd: () => { this._changed(); this._renderHandles(); this._emit('radius_changed'); },
        });

        this._centerHandle = center;
    }

    _moveRadiusHandle() {
        const edge = eastOf(this.center, this.radius);
        this._radiusHandle?.setLngLat([edge.lng(), edge.lat()]);
    }
}

export class Polygon extends Shape {
    constructor(options = {}) {
        super(options);
        const paths = options.paths || [];
        const first = Array.isArray(paths[0]) ? paths[0] : paths;
        this._path = new MVCPath(first);
        delete this.paths;
        if (this._pendingMap) this.setMap(this._pendingMap);
    }

    getPath() { return this._path; }

    getPaths() { return [this._path]; }

    _ring() {
        const points = this._path.getArray().map((p) => [p.lng(), p.lat()]);
        if (points.length < 3) return null;

        return [...points, points[0]];
    }

    _buildHandles() {
        const points = this._path.getArray();

        points.forEach((point, index) => {
            this._makeHandle([point.lng(), point.lat()], 'geo-mb-handle', {
                onDrag: (ll) => { this._path._points[index] = new LatLng(ll.lat, ll.lng); this._map._refreshShapes(false); },
                onDragEnd: (ll) => { this._path.setAt(index, { lat: ll.lat, lng: ll.lng }); this._renderHandles(); },
            });

            const next = points[(index + 1) % points.length];
            const mid = [(point.lng() + next.lng()) / 2, (point.lat() + next.lat()) / 2];

            this._makeHandle(mid, 'geo-mb-handle geo-mb-handle-mid', {
                onDragEnd: (ll) => { this._path.insertAt(index + 1, { lat: ll.lat, lng: ll.lng }); this._changed(); this._renderHandles(); },
            });
        });
    }
}

export class Rectangle extends Shape {
    constructor(options = {}) {
        super(options);
        this._bounds = options.bounds || new LatLngBounds();
        delete this.bounds;
        if (this._pendingMap) this.setMap(this._pendingMap);
    }

    getBounds() { return this._bounds; }

    setBounds(bounds) {
        this._bounds = bounds;
        this._changed();
        this._emit('bounds_changed');
    }

    setOptions(options = {}) {
        const { bounds, ...rest } = options;
        Object.assign(this, rest);
        if (bounds) this._bounds = bounds;
        this._changed();
        this._renderHandles();
    }

    _ring() {
        if (this._bounds.isEmpty()) return null;
        const ne = this._bounds.getNorthEast();
        const sw = this._bounds.getSouthWest();

        return [
            [sw.lng(), sw.lat()], [sw.lng(), ne.lat()], [ne.lng(), ne.lat()], [ne.lng(), sw.lat()], [sw.lng(), sw.lat()],
        ];
    }

    _buildHandles() {
        const ne = this._bounds.getNorthEast();
        const sw = this._bounds.getSouthWest();
        const corners = [
            { lng: sw.lng(), lat: sw.lat(), opposite: [ne.lng(), ne.lat()] },
            { lng: sw.lng(), lat: ne.lat(), opposite: [ne.lng(), sw.lat()] },
            { lng: ne.lng(), lat: ne.lat(), opposite: [sw.lng(), sw.lat()] },
            { lng: ne.lng(), lat: sw.lat(), opposite: [sw.lng(), ne.lat()] },
        ];

        corners.forEach((corner) => {
            const apply = (ll) => new LatLngBounds({ lat: ll.lat, lng: ll.lng }, { lat: corner.opposite[1], lng: corner.opposite[0] });

            this._makeHandle([corner.lng, corner.lat], 'geo-mb-handle', {
                onDrag: (ll) => { this._bounds = apply(ll); this._map._refreshShapes(false); },
                onDragEnd: (ll) => { this.setBounds(apply(ll)); this._renderHandles(); },
            });
        });
    }
}

export class Marker {
    constructor(options = {}) {
        this._position = options.position ? toLatLng(options.position) : null;
    }

    getPosition() { return this._position; }

    setMap() {}
}

/* =============================== Geocodificação ============================== */

class Geocoder {
    geocode({ address }, callback) {
        const token = window.mapboxApiKey;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`
            + `?access_token=${token}&country=br&limit=1&language=pt`;

        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                const feature = data.features?.[0];
                if (!feature) { callback([], 'ZERO_RESULTS'); return; }

                const [lng, lat] = feature.center;
                callback([{
                    formatted_address: feature.place_name,
                    geometry: { location: new LatLng(lat, lng) },
                }], 'OK');
            })
            .catch(() => callback([], 'UNKNOWN_ERROR'));
    }
}

/* ================================ Mapa (shim) ================================ */

/**
 * Faz o papel de `google.maps.Map` (o que o app recebe em `window.googleMap`), sobre um
 * `mapboxgl.Map`. Também é o dono da camada que desenha as formas.
 */
export class MapboxMapShim {
    constructor(mapbox) {
        this._mapbox = mapbox;
        this._shapes = new Set();
        this.isDrawing = false;
        // O app acessa o mapa cru por `window.googleMap.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED`.
        this.context = { __SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED: this };
    }

    /* --- navegação --- */

    panTo(position) {
        const { _lat, _lng } = toLatLng(position);
        this._mapbox.easeTo({ center: [_lng, _lat] });
    }

    setZoom(zoom) { this._mapbox.setZoom(zoom); }

    getZoom() { return this._mapbox.getZoom(); }

    getCenter() {
        const center = this._mapbox.getCenter();

        return new LatLng(center.lat, center.lng);
    }

    fitBounds(bounds) {
        if (!bounds || bounds.isEmpty()) return;
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        this._mapbox.fitBounds([[sw.lng(), sw.lat()], [ne.lng(), ne.lat()]], { padding: 60, maxZoom: 17, duration: 600 });
    }

    /** Só a opção `disableDefaultUI` é usada (esconde controles e alças durante a captura). */
    setOptions(options = {}) {
        if ('disableDefaultUI' in options) {
            this._mapbox.getContainer().classList.toggle('geo-mb-hide-ui', !!options.disableDefaultUI);
        }
    }

    /** Copia o frame atual do mapa (o canvas do mapa continua mudando depois). */
    async captureCanvas() {
        await new Promise((resolve) => {
            this._mapbox.once('idle', resolve);
            this._mapbox.triggerRepaint();
        });

        const source = this._mapbox.getCanvas();
        const copy = document.createElement('canvas');
        copy.width = source.width;
        copy.height = source.height;
        copy.getContext('2d').drawImage(source, 0, 0);

        return copy;
    }

    /* --- formas --- */

    _attach(shape) {
        this._shapes.add(shape);
        this._refreshShapes();
        shape._renderHandles();
    }

    _detach(shape) {
        this._shapes.delete(shape);
        this._refreshShapes();
    }

    ensureShapeLayers() {
        const map = this._mapbox;
        if (!map.getSource(SHAPES_SOURCE)) {
            map.addSource(SHAPES_SOURCE, { type: 'geojson', data: EMPTY_FC });
        }

        if (!map.getLayer(SHAPES_FILL_LAYER)) {
            map.addLayer({
                id: SHAPES_FILL_LAYER,
                type: 'fill',
                source: SHAPES_SOURCE,
                paint: { 'fill-color': ['get', 'fillColor'], 'fill-opacity': ['get', 'fillOpacity'] },
            });
            map.addLayer({
                id: SHAPES_LINE_LAYER,
                type: 'line',
                source: SHAPES_SOURCE,
                paint: { 'line-color': ['get', 'strokeColor'], 'line-width': ['get', 'strokeWeight'] },
            });
        }

        this._refreshShapes();
    }

    _refreshShapes() {
        const source = this._mapbox.getSource(SHAPES_SOURCE);
        if (!source) return;

        const features = [];
        this._shapes.forEach((shape) => {
            const feature = shape._feature();
            if (feature) features.push(feature);
        });

        source.setData({ type: 'FeatureCollection', features });
    }

    /** Clique numa forma clicável (as desenhadas ficam com `clickable: false`, como no Google). */
    handleMapClick(lngLat) {
        if (this.isDrawing) return false;
        const point = this._mapbox.project(lngLat);
        const hits = this._mapbox.queryRenderedFeatures(point, { layers: [SHAPES_FILL_LAYER] });
        const shape = [...this._shapes].find((s) => hits.some((h) => h.properties.uid === s._uid) && s.clickable !== false);
        if (!shape) return false;
        shape._emit('click', { latLng: new LatLng(lngLat.lat, lngLat.lng) });

        return true;
    }
}

/* =============================== Instalação global ============================ */

export function installGoogleCompat() {
    if (window.google?.maps?.Circle?.__geoCompat) return;

    Circle.__geoCompat = true;
    window.google = {
        maps: {
            LatLng, LatLngBounds, Circle, Polygon, Rectangle, Marker, Geocoder, event,
        },
    };
}
